"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FavoritesProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const flora_react_1 = require("@grupoboticario/flora-react");
const flora_react_icons_1 = require("@grupoboticario/flora-react-icons");
const react_1 = require("react");
const useCustomer_1 = require("../../hooks/useCustomer");
const favorites_1 = require("../../repositories/favorites");
const analytics_1 = require("../../services/analytics");
const favorites_2 = require("../../services/favorites");
const tdcClient_1 = require("../../services/tdcClient");
const favorites_3 = require("../../usecases/favorites");
const utils_1 = require("../../utils");
const query_1 = require("../query");
const favorites_context_1 = require("./favorites.context");
const FavoritesLocalStorageKey = 'wishlist';
const FavoritesLocalItemLimit = 25;
const DefaultFavoritesListName = 'favoritos';
function FavoritesWithQueryProvider({ children, businessUnit, queryContext, resellerSlug, }) {
    const customerState = (0, useCustomer_1.useCustomer)();
    const isSynchronizingFavoritesRef = (0, react_1.useRef)(false);
    const isFavoritesEnabled = (0, react_1.useMemo)(() => !(0, utils_1.isGenericReseller)(resellerSlug), [resellerSlug]);
    const tagService = (0, react_1.useMemo)(() => new favorites_2.FavoritesTagService(analytics_1.VWOAnalyticsService, analytics_1.GoogleAnalyticsService), []);
    const favoritesLocalStorageRepository = (0, react_1.useMemo)(() => new favorites_1.FavoritesRepositoryLocalStorageImpl(FavoritesLocalStorageKey, FavoritesLocalItemLimit), []);
    const favoritesRepository = (0, react_1.useMemo)(() => {
        const customerId = customerState.customerId;
        const shouldUseApiRepository = isFavoritesEnabled && customerState.isRecognized && customerId;
        if (shouldUseApiRepository) {
            const favoritesService = new favorites_2.FavoritesService(tdcClient_1.tdcClient);
            return new favorites_1.FavoritesRepositoryApiImpl(favoritesService, {
                businessUnit,
                customerId,
                listName: DefaultFavoritesListName,
                resellerSlug,
            });
        }
        return favoritesLocalStorageRepository;
    }, [
        customerState.isRecognized,
        customerState.customerId,
        favoritesLocalStorageRepository,
        businessUnit,
        resellerSlug,
    ]);
    const [favoritesUseCaseResult, getFavoritesUseCase] = (0, favorites_3.useGetFavoritesUseCase)({
        favoritesRepository,
        queryContext,
    });
    const notifyFavorite = (description, kind) => {
        flora_react_1.Toaster.notify({
            kind,
            description,
            origin: 'center-top',
            Icon: (0, jsx_runtime_1.jsx)(flora_react_icons_1.HeartFillIcon, {}),
        });
    };
    const notifyUnfavorite = (description, kind) => {
        flora_react_1.Toaster.notify({
            kind,
            description,
            origin: 'center-top',
            Icon: (0, jsx_runtime_1.jsx)(flora_react_icons_1.MinusCircleIcon, {}),
        });
    };
    const favoriteUseCase = (0, favorites_3.useFavoriteUseCase)({
        favoritesRepository,
        tagService,
        queryContext,
        onSuccess() {
            notifyFavorite('Produto adicionado aos favoritos', 'success');
        },
        onError() {
            notifyFavorite('Erro ao adicionar produto aos favoritos', 'error');
        },
    });
    const unfavoriteUseCase = (0, favorites_3.useUnfavoriteUseCase)({
        favoritesRepository,
        queryContext,
        tagService,
        onSuccess() {
            notifyUnfavorite('Produto excluído dos favoritos', 'success');
        },
        onError() {
            notifyUnfavorite('Erro ao excluir produto dos favoritos', 'error');
        },
    });
    const favorites = (0, react_1.useMemo)(() => (isFavoritesEnabled ? favoritesUseCaseResult : []), [isFavoritesEnabled, favoritesUseCaseResult]);
    const totalOfFavorites = (0, react_1.useMemo)(() => favorites.length, [favorites]);
    const hasFavorites = (0, react_1.useMemo)(() => !!totalOfFavorites, [favorites]);
    const favorite = (product) => __awaiter(this, void 0, void 0, function* () {
        if (!isFavoritesEnabled) {
            throw new Error('Favorites are not enabled');
        }
        return yield favoriteUseCase(product);
    });
    const unfavorite = (product) => __awaiter(this, void 0, void 0, function* () {
        if (!isFavoritesEnabled) {
            throw new Error('Favorites are not enabled');
        }
        return yield unfavoriteUseCase(product);
    });
    const isFull = () => __awaiter(this, void 0, void 0, function* () {
        if (!isFavoritesEnabled) {
            return true;
        }
        return yield favoritesRepository.isFull();
    });
    const isFavorited = (sku) => __awaiter(this, void 0, void 0, function* () {
        if (!isFavoritesEnabled) {
            return false;
        }
        return favorites.some((item) => item.sku === sku);
    });
    const toggleFavorite = (product) => __awaiter(this, void 0, void 0, function* () {
        const operation = (yield isFavorited(product.sku)) ? unfavorite : favorite;
        return yield operation(product);
    });
    const synchronizeFavoritesUseCase = (0, favorites_3.useSynchronizeFavoritesUseCase)({
        businessUnit,
        favoritesLocalStorageRepository,
        resellerSlug,
        onSuccess: () => {
            getFavoritesUseCase();
        },
    });
    (0, react_1.useEffect)(() => {
        if (isFavoritesEnabled) {
            getFavoritesUseCase();
        }
    }, [isFavoritesEnabled, favoritesRepository.strategyName, getFavoritesUseCase]);
    (0, react_1.useEffect)(() => {
        const shouldSynchronize = isFavoritesEnabled &&
            customerState.isRecognized &&
            customerState.customerId &&
            favoritesRepository instanceof favorites_1.FavoritesRepositoryApiImpl &&
            !isSynchronizingFavoritesRef.current;
        if (shouldSynchronize) {
            isSynchronizingFavoritesRef.current = true;
            synchronizeFavoritesUseCase(favoritesRepository).finally(() => {
                isSynchronizingFavoritesRef.current = false;
            });
        }
    }, [
        isFavoritesEnabled,
        customerState.isRecognized,
        customerState.customerId,
        favoritesRepository,
        isSynchronizingFavoritesRef.current,
        synchronizeFavoritesUseCase,
    ]);
    return ((0, jsx_runtime_1.jsx)(favorites_context_1.FavoritesContext.Provider, Object.assign({ value: {
            favorites,
            hasFavorites,
            isFavoritesEnabled,
            totalOfFavorites,
            favorite,
            isFavorited,
            isFull,
            toggleFavorite,
            unfavorite,
        } }, { children: children })));
}
function FavoritesProvider({ children, businessUnit, resellerSlug, queryContext = favorites_context_1.favoritesQueryClientContext, }) {
    return ((0, jsx_runtime_1.jsx)(query_1.BarrilQueryProvider, { children: (0, jsx_runtime_1.jsx)(FavoritesWithQueryProvider, Object.assign({ businessUnit: businessUnit, resellerSlug: resellerSlug, queryContext: queryContext }, { children: children })) }));
}
exports.FavoritesProvider = FavoritesProvider;
